/**
 * TranslationOverlay Component
 *
 * Main container that orchestrates all translation visual components:
 * - Arabic text panels (left side - original text, selectable)
 * - Paragraph highlights on PDF pages
 * - Connection lines (SVG bezier curves)
 * - Translation blobs (right side - English text)
 *
 * Handles:
 * - Coordinate conversion from normalized to viewport
 * - Hover state synchronization across all components
 * - Scroll and zoom adjustments
 *
 * Layout:
 * [Arabic Panel] --- [PDF Page with Highlights] --- [English Translation]
 *     LEFT                    CENTER                      RIGHT
 */

import React, { useState, useMemo } from "react";
import { PdfParagraph, TranslatedParagraph } from "./translationTypes.js";
import { TranslationBlob } from "./TranslationBlob.js";
import { ConnectionLine } from "./ConnectionLine.js";
import { ParagraphHighlight } from "./ParagraphHighlight.js";
import { ArabicTextPanel } from "./ArabicTextPanel.js";

// Layout constants for the Arabic text panel (left side)
const ARABIC_PANEL_WIDTH = 280;
const ARABIC_PANEL_GAP = 24; // Gap between page left edge and Arabic panel
const TOOLBAR_HEIGHT = 32; // Height of the PDF.js toolbar to avoid overlapping

interface TranslationOverlayProps {
  /** All paragraphs from the translated pages */
  paragraphs: PdfParagraph[];
  /** Translations for the paragraphs */
  translations: TranslatedParagraph[];
  /** X position for translation blobs (right edge of PDF + margin) */
  marginX: number;
  /** Current viewer scroll position */
  viewerScroll: { top: number; left: number };
  /** Current viewer scale */
  scale: number;
  /** Reference to iframe for querying page elements */
  iframeRef: React.RefObject<HTMLIFrameElement>;
  /**
   * Page indices to actually render overlays for.
   * If provided, only these pages will have overlays drawn.
   * If not provided, overlays are drawn for all pages with translations.
   *
   * This prevents the "translations disappeared" issue when navigating
   * to pages where the PDF.js DOM may not have the page element rendered.
   */
  visiblePageIndices?: number[];
  /** Whether to show the Arabic text panels on the left side. Default true. */
  showArabicText?: boolean;
  /** Whether to show the English translations on the right side. Default true. */
  showEnglishTranslations?: boolean;
}

/**
 * Get page element bounds from the PDF.js viewer iframe.
 */
function getPageBounds(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  pageIndex: number
): { width: number; height: number; offsetX: number; offsetY: number } | null {
  if (!iframeRef.current?.contentDocument) {
    return null;
  }

  const pageElement = iframeRef.current.contentDocument.querySelector(
    `.page[data-page-number="${pageIndex + 1}"]`
  ) as HTMLElement | null;

  if (!pageElement) {
    return null;
  }

  const rect = pageElement.getBoundingClientRect();
  const iframeRect = iframeRef.current.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    offsetX: rect.left - iframeRect.left + iframeRect.left,
    offsetY: rect.top - iframeRect.top + iframeRect.top,
  };
}

export function TranslationOverlay({
  paragraphs,
  translations,
  marginX,
  viewerScroll,
  scale,
  iframeRef,
  visiblePageIndices,
  showArabicText = true,
  showEnglishTranslations = true,
}: TranslationOverlayProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Create lookup map for translations by paragraph ID
  const translationMap = useMemo(() => {
    const map = new Map<string, TranslatedParagraph>();
    for (const t of translations) {
      map.set(t.paragraphId, t);
    }
    return map;
  }, [translations]);

  // Group paragraphs by page for efficient rendering
  const paragraphsByPage = useMemo(() => {
    const byPage = new Map<number, PdfParagraph[]>();
    for (const p of paragraphs) {
      const existing = byPage.get(p.pageIndex) || [];
      existing.push(p);
      byPage.set(p.pageIndex, existing);
    }
    return byPage;
  }, [paragraphs]);

  // Determine which pages to render overlays for
  // If visiblePageIndices is provided, only render those pages
  // Otherwise, render all pages that have translations
  const visiblePagesSet = useMemo(
    () => new Set(visiblePageIndices ?? Array.from(paragraphsByPage.keys())),
    [visiblePageIndices, paragraphsByPage]
  );

  // Render all components
  const elements: React.ReactNode[] = [];

  for (const [pageIndex, pageParagraphs] of paragraphsByPage) {
    // Skip pages that are not in the visible set
    if (!visiblePagesSet.has(pageIndex)) continue;

    const pageBounds = getPageBounds(iframeRef, pageIndex);
    if (!pageBounds) {
      // Page element not found in DOM - PDF.js may not have rendered it yet
      // This is expected when scrolling; the page will be rendered when visible
      continue;
    }

    // Calculate Arabic panel X position (left of the PDF page)
    // Ensure it doesn't go off-screen (minimum 16px from left edge)
    const arabicPanelLeft = Math.max(
      16,
      pageBounds.offsetX - ARABIC_PANEL_WIDTH - ARABIC_PANEL_GAP
    );

    for (const para of pageParagraphs) {
      const translation = translationMap.get(para.id);
      if (!translation) continue;

      const isHovered = hoveredId === para.id;

      // Calculate positions
      // Paragraph center in viewport coordinates (adjusted for container offset)
      const paraY =
        pageBounds.offsetY + (para.bbox.y0 + para.bbox.y1) / 2 * pageBounds.height - TOOLBAR_HEIGHT;
      const paraRightX = pageBounds.offsetX + para.bbox.x1 * pageBounds.width;

      // Blob position (aligned with paragraph top, adjusted for container offset)
      const blobTop = pageBounds.offsetY + para.bbox.y0 * pageBounds.height - TOOLBAR_HEIGHT;
      const blobCenterY = blobTop + 40; // Approximate center of blob

      // Arabic panel position (aligned with paragraph top, same as English blob)
      const arabicTop = blobTop;

      // Add Arabic text panel (left side) - only if enabled
      if (showArabicText) {
        elements.push(
          <ArabicTextPanel
            key={`arabic-${para.id}`}
            paragraph={para}
            translation={translation}
            top={arabicTop}
            left={arabicPanelLeft}
            width={ARABIC_PANEL_WIDTH}
            isHovered={isHovered}
            onHover={(hovering) => setHoveredId(hovering ? para.id : null)}
          />
        );
      }

      // Add highlight on the PDF (adjusted for container offset)
      elements.push(
        <ParagraphHighlight
          key={`highlight-${para.id}`}
          paragraph={para}
          colorIndex={translation.colorIndex}
          isHovered={isHovered}
          pageWidth={pageBounds.width}
          pageHeight={pageBounds.height}
          pageOffsetX={pageBounds.offsetX}
          pageOffsetY={pageBounds.offsetY - TOOLBAR_HEIGHT}
        />
      );

      // Add connection line and translation blob (right side - English) only if enabled
      if (showEnglishTranslations) {
        // Add connection line (from paragraph to English blob)
        elements.push(
          <ConnectionLine
            key={`line-${para.id}`}
            startX={paraRightX + 8} // Small gap from paragraph
            startY={paraY}
            endX={marginX - 4} // Small gap from blob
            endY={blobCenterY}
            colorIndex={translation.colorIndex}
            isHovered={isHovered}
          />
        );

        // Add translation blob (right side - English)
        elements.push(
          <TranslationBlob
            key={`blob-${para.id}`}
            translation={translation}
            top={blobTop}
            marginX={marginX}
            isHovered={isHovered}
            onHover={(hovering) => setHoveredId(hovering ? para.id : null)}
          />
        );
      }
    }
  }

  return (
    <div
      className="translationOverlay"
      style={{
        position: "absolute",
        top: TOOLBAR_HEIGHT, // Start below the PDF.js toolbar
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 90,
        overflow: "hidden", // Clip elements that would extend above the container
      }}
    >
      {/* Enable pointer events only for interactive children */}
      <div style={{ pointerEvents: "auto" }}>{elements}</div>
    </div>
  );
}

export default TranslationOverlay;
